import React from 'react';
import ReactFlow, { MiniMap, Controls, Background, Position, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import '../../App.css';

const themeStyles = {
  objectiveNode: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: '2px solid #5a67d8',
    borderRadius: '12px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ffffff',
    width: 200,
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(90, 103, 216, 0.3)',
  },
  populationNode: {
    background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
    border: '2px solid #e53e3e',
    borderRadius: '15px',
    padding: '18px 25px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#2d3748',
    width: 240,
    textAlign: 'center',
    boxShadow: '0 6px 20px rgba(229, 62, 62, 0.25)',
  },
  solutionNode: {
    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    border: '2px solid #38b2ac',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '12px',
    color: '#2d3748',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 3px 10px rgba(56, 178, 172, 0.2)',
  },
  processNode: {
    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    border: '2px solid #ed8936',
    borderRadius: '12px',
    padding: '14px 20px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#2d3748',
    width: 280,
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(237, 137, 54, 0.2)',
  },
  paretoNode: {
    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    border: '3px solid #319795',
    borderRadius: '15px',
    padding: '20px 25px',
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#2d3748',
    width: 280,
    textAlign: 'center',
    boxShadow: '0 6px 25px rgba(49, 151, 149, 0.3)',
  },
  groupNode: {
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    border: '2px dashed #667eea',
    borderRadius: '15px',
    padding: '25px',
  },
  edgeStyle: {
    stroke: '#667eea',
    strokeWidth: 3,
  },
  conflictEdge: {
    stroke: '#e53e3e',
    strokeWidth: 2,
    strokeDasharray: '8,4',
  },
  paretoEdge: {
    stroke: '#319795',
    strokeWidth: 4,
  },
};

const initialNodes = [
  // Problem Definition
  {
    id: 'problem-def',
    data: { label: '🎯 Multi-Objective Problem\nDefinition' },
    position: { x: 600, y: 50 },
    sourcePosition: Position.Bottom,
    style: themeStyles.processNode,
  },

  // Multiple Objectives Group
  {
    id: 'objectives-group',
    type: 'group',
    data: { label: 'Conflicting Objectives' },
    position: { x: 200, y: 150 },
    style: { ...themeStyles.groupNode, width: 800, height: 120 },
  },
  {
    id: 'obj1',
    data: { label: '⚡ Performance\n(Execution Speed)' },
    position: { x: 20, y: 40 },
    parentNode: 'objectives-group',
    extent: 'parent',
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.objectiveNode,
  },
  {
    id: 'obj2',
    data: { label: '💾 Memory Usage\n(Resource Efficiency)' },
    position: { x: 280, y: 40 },
    parentNode: 'objectives-group',
    extent: 'parent',
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.objectiveNode,
  },
  {
    id: 'obj3',
    data: { label: '📚 Code Readability\n(Maintainability)' },
    position: { x: 540, y: 40 },
    parentNode: 'objectives-group',
    extent: 'parent',
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.objectiveNode,
  },

  // Initial Population
  {
    id: 'initial-pop',
    data: { label: '🧬 Initial Population\n(Random Solutions)' },
    position: { x: 50, y: 350 },
    sourcePosition: Position.Right,
    targetPosition: Position.Bottom,
    style: themeStyles.populationNode,
  },

  // Multi-Objective Evaluation
  {
    id: 'mo-evaluation',
    data: { label: '🔍 Multi-Objective Evaluation\n(Fitness for Each Objective)' },
    position: { x: 550, y: 350 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.processNode,
  },

  // Solution Space Group
  {
    id: 'solution-space',
    type: 'group',
    data: { label: 'Solution Space Exploration' },
    position: { x: 100, y: 450 },
    style: { ...themeStyles.groupNode, width: 350, height: 200 },
  },
  {
    id: 'sol1',
    data: { label: 'Solution A\n⚡High | 💾High | 📚Low' },
    position: { x: 20, y: 40 },
    parentNode: 'solution-space',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.solutionNode,
  },
  {
    id: 'sol2',
    data: { label: 'Solution B\n⚡Med | 💾Low | 📚High' },
    position: { x: 180, y: 40 },
    parentNode: 'solution-space',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.solutionNode,
  },
  {
    id: 'sol3',
    data: { label: 'Solution C\n⚡Low | 💾Med | 📚Med' },
    position: { x: 20, y: 120 },
    parentNode: 'solution-space',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.solutionNode,
  },
  {
    id: 'sol4',
    data: { label: 'Solution D\n⚡High | 💾Med | 📚High' },
    position: { x: 180, y: 120 },
    parentNode: 'solution-space',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.solutionNode,
  },

  // NSGA-II Process
  {
    id: 'nsga2',
    data: { label: '🧠 NSGA-II Algorithm\n(Non-dominated Sorting)' },
    position: { x: 550, y: 500 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.processNode,
  },

  // Pareto Front
  {
    id: 'pareto-front',
    data: { label: '📈 Pareto Front\n(Non-dominated Solutions)' },
    position: { x: 900, y: 500 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: themeStyles.paretoNode,
  },

  // Trade-off Analysis
  {
    id: 'tradeoff-analysis',
    data: { label: '⚖️ Trade-off Analysis\n(Decision Making Support)' },
    position: { x: 900, y: 650 },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: themeStyles.processNode,
  },

  // Evolutionary Operations Group
  {
    id: 'evo-ops-group',
    type: 'group',
    data: { label: 'Evolutionary Operations' },
    position: { x: 200, y: 700 },
    style: { ...themeStyles.groupNode, width: 600, height: 120 },
  },
  {
    id: 'selection',
    data: { label: '🎯 Selection\n(Tournament/Ranking)' },
    position: { x: 20, y: 40 },
    parentNode: 'evo-ops-group',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: themeStyles.processNode,
  },
  {
    id: 'crossover',
    data: { label: '🧬 Crossover\n(Multi-point)' },
    position: { x: 210, y: 40 },
    parentNode: 'evo-ops-group',
    extent: 'parent',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },
  {
    id: 'mutation',
    data: { label: '🧪 Mutation\n(Adaptive Rate)' },
    position: { x: 400, y: 40 },
    parentNode: 'evo-ops-group',
    extent: 'parent',
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: themeStyles.processNode,
  },

  // Next Generation
  {
    id: 'next-gen',
    data: { label: '🔄 Next Generation\n(Improved Solutions)' },
    position: { x: 350, y: 900 },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: themeStyles.populationNode,
  },

  // Final Solutions
  {
    id: 'final-solutions',
    data: { label: '🏆 Optimal Solution Set\n(Pareto-Optimal)' },
    position: { x: 900, y: 800 },
    targetPosition: Position.Top,
    style: themeStyles.paretoNode,
  },

  // Convergence Check
  {
    id: 'convergence',
    data: { label: '🎯 Convergence Check\n(Stopping Criteria)' },
    position: { x: 650, y: 900 },
    targetPosition: Position.Left,
    sourcePosition: Position.Right,
    style: themeStyles.processNode,
  },
];

const initialEdges = [
  // Problem to objectives
  { id: 'e1', source: 'problem-def', target: 'obj1', style: themeStyles.edgeStyle, animated: true, label: 'defines' },
  { id: 'e2', source: 'problem-def', target: 'obj2', style: themeStyles.edgeStyle, animated: true },
  { id: 'e3', source: 'problem-def', target: 'obj3', style: themeStyles.edgeStyle, animated: true },

  // Initial population to solution space
  { id: 'e4', source: 'initial-pop', target: 'sol1', style: themeStyles.edgeStyle, animated: true, label: 'generates' },
  { id: 'e5', source: 'initial-pop', target: 'sol2', style: themeStyles.edgeStyle, animated: true },

  // Objectives to evaluation (showing conflicts)
  { id: 'e6', source: 'obj1', target: 'mo-evaluation', style: themeStyles.conflictEdge, animated: true, label: 'conflicts' },
  { id: 'e7', source: 'obj2', target: 'mo-evaluation', style: themeStyles.conflictEdge, animated: true },
  { id: 'e8', source: 'obj3', target: 'mo-evaluation', style: themeStyles.conflictEdge, animated: true },

  // Solutions to evaluation
  { id: 'e9', source: 'sol4', target: 'mo-evaluation', style: themeStyles.edgeStyle, animated: true, label: 'evaluates' },

  // Evaluation to NSGA-II
  { id: 'e10', source: 'mo-evaluation', target: 'nsga2', style: themeStyles.edgeStyle, animated: true, label: 'fitness scores' },

  // NSGA-II to Pareto front
  { id: 'e11', source: 'nsga2', target: 'pareto-front', style: themeStyles.paretoEdge, animated: true, label: 'identifies' },

  // Pareto front to trade-off analysis
  { id: 'e12', source: 'pareto-front', target: 'tradeoff-analysis', style: themeStyles.edgeStyle, animated: true, label: 'enables' },

  // NSGA-II to evolutionary operations
  { id: 'e13', source: 'nsga2', target: 'selection', style: themeStyles.edgeStyle, animated: true, label: 'guides' },
  { id: 'e14', source: 'selection', target: 'crossover', style: themeStyles.edgeStyle, animated: true },
  { id: 'e15', source: 'crossover', target: 'mutation', style: themeStyles.edgeStyle, animated: true },

  // Evolution to next generation
  { id: 'e16', source: 'mutation', target: 'next-gen', style: themeStyles.edgeStyle, animated: true, label: 'creates' },

  // Feedback loop
  { id: 'e17', source: 'next-gen', target: 'mo-evaluation', style: { ...themeStyles.edgeStyle, strokeDasharray: '6,6' }, animated: true, label: 'iterates' },

  // Trade-off to final solutions
  { id: 'e18', source: 'tradeoff-analysis', target: 'final-solutions', style: themeStyles.paretoEdge, animated: true, label: 'selects' },

  // Final solutions to convergence
  { id: 'e19', source: 'final-solutions', target: 'convergence', style: themeStyles.edgeStyle, animated: true, label: 'checks' },

  // Convergence feedback
  { id: 'e20', source: 'convergence', target: 'next-gen', style: { ...themeStyles.edgeStyle, strokeDasharray: '4,4' }, animated: false, label: 'continue/stop' },
];

const MultiObjectiveOptimisation = ({ title }) => (
  <ReactFlowProvider>
    <div style={{ 
      height: 'calc(100vh - 60px)', 
      width: '100%', 
      pointerEvents: 'all', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          paddingTop: '15px', 
          marginBottom: '10px', 
          fontFamily: 'Arial, sans-serif',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          {title || "Multi-Objective Optimization: Balancing Conflicting Goals"}
        </h2>
        <ReactFlow
          nodes={initialNodes}
          edges={initialEdges}
          fitView
          nodesDraggable={true}
          elementsSelectable={true}
          defaultEdgeOptions={{ animated: true, style: themeStyles.edgeStyle }}
        >
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'group') return '#667eea20';
              if (node.style && node.style.background) {
                if (typeof node.style.background === 'string' && node.style.background.includes('gradient')) {
                  return '#667eea';
                }
                return node.style.background;
              }
              return '#667eea';
            }}
            nodeStrokeColor={() => '#667eea'}
            nodeBorderRadius={8}
            zoomable 
            pannable 
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '2px solid #667eea',
              borderRadius: '12px'
            }}
          />
          <Controls 
            style={{
              button: {
                backgroundColor: 'rgba(102, 126, 234, 0.9)',
                border: 'none',
                borderRadius: '8px'
              }
            }}
          />
          <Background 
            color="#667eea" 
            gap={25} 
            size={2}
            style={{ opacity: 0.1 }}
          />
        </ReactFlow>
      </div>
    </div>
  </ReactFlowProvider>
);

export default MultiObjectiveOptimisation; 